import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * Choosing which of a shopper's lists a product belongs on:
 *
 *   <FavouritesPicker>
 *     <FavouritesPickerTitle>Vyberte seznam</FavouritesPickerTitle>
 *     <FavouritesPickerPanel>
 *       <FavouritesPickerOptions>
 *         <FavouritesPickerOption>
 *           <FavouritesPickerOptionLabel htmlFor={list.id}>
 *             <Checkbox id={list.id} checked={…} onCheckedChange={…} />
 *             <FavouritesPickerOptionName>{list.name}</FavouritesPickerOptionName>
 *           </FavouritesPickerOptionLabel>
 *         </FavouritesPickerOption>
 *       </FavouritesPickerOptions>
 *     </FavouritesPickerPanel>
 *   </FavouritesPicker>
 *
 * **Three things can stand in the panel, and which one is the tenant's read.**
 * The lists, a spinner while they are on their way, or a sentence saying there
 * are none — three parts, written by the composition that knows which state its
 * request is in. Not an arm, because they are different things rather than one
 * thing painted differently.
 *
 * **Nothing about saving crosses into this package.** Which lists exist, which
 * already hold the product, what a tick does and what is sent when the shopper
 * is finished are all the tenant's; the checkbox itself is
 * `@vs-shop/ui/checkbox`, composed in.
 *
 * **The list of lists is bounded and scrolls.** A modal is a fixed-height
 * surface with a footer that must stay reachable, so the options take a ceiling
 * rather than pushing the submit off the bottom for a shopper with thirty
 * lists.
 *
 * Server-first: every part here is a plain component.
 */

/** The block: what it asks for, above where the answer is given. */
const favouritesPickerClasses = 'flex flex-col gap-2.5';

export type FavouritesPickerProps = ComponentProps<'div'>;

/** The block a shopper picks lists in. */
export const FavouritesPicker = ({ className, ...props }: FavouritesPickerProps) => (
  <div
    data-slot="favourites-picker"
    className={clsxm(favouritesPickerClasses, className)}
    {...props}
  />
);

/** What the block asks. A neutral element: the overlay's outline is the overlay's. */
const favouritesPickerTitleClasses = 'text-lg';

export type FavouritesPickerTitleProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral `<div>`. */
  readonly render?: RenderElement;
};

/** The block's question. */
export const FavouritesPickerTitle = ({
  className,
  render,
  ...props
}: FavouritesPickerTitleProps) => (
  <RenderedPart
    dataSlot="favourites-picker-title"
    className={clsxm(favouritesPickerTitleClasses, className)}
    render={render}
    {...props}
  />
);

/** The framed ground the answer is given on, set off from the overlay around it. */
const favouritesPickerPanelClasses =
  'border-foreground/10 bg-background-intense-more rounded-lg border p-2.5';

export type FavouritesPickerPanelProps = ComponentProps<'div'>;

/** Where the lists stand. */
export const FavouritesPickerPanel = ({ className, ...props }: FavouritesPickerPanelProps) => (
  <div
    data-slot="favourites-picker-panel"
    className={clsxm(favouritesPickerPanelClasses, className)}
    {...props}
  />
);

/**
 * The lists, bounded so the overlay's footer stays reachable.
 *
 * The ceiling is the part's own: how many lists a shopper has is not something
 * a composition can know, and a panel that grew with them would push the submit
 * off the surface.
 */
const favouritesPickerOptionsClasses = 'flex max-h-40 flex-col gap-3 overflow-y-auto';

export type FavouritesPickerOptionsProps = ComponentProps<'ul'>;

/** The lists to pick from. */
export const FavouritesPickerOptions = ({ className, ...props }: FavouritesPickerOptionsProps) => (
  <ul
    data-slot="favourites-picker-options"
    className={clsxm(favouritesPickerOptionsClasses, className)}
    {...props}
  />
);

/**
 * One list's place.
 *
 * As wide as its own contents and no wider, so the target a shopper presses
 * ends where the name does rather than running the width of the panel.
 */
const favouritesPickerOptionClasses = 'w-fit';

export type FavouritesPickerOptionProps = ComponentProps<'li'>;

/** One list in the panel. */
export const FavouritesPickerOption = ({ className, ...props }: FavouritesPickerOptionProps) => (
  <li
    data-slot="favourites-picker-option"
    className={clsxm(favouritesPickerOptionClasses, className)}
    {...props}
  />
);

/** The tick and the name as one target. */
const favouritesPickerOptionLabelClasses = 'flex cursor-pointer gap-2';

export type FavouritesPickerOptionLabelProps = ComponentProps<'label'>;

/**
 * What a shopper presses to put the product on a list.
 *
 * A real `<label>` over the composed checkbox, so the name is part of the
 * target and the browser gives the pairing for free; which control it points at
 * is `htmlFor`, riding the native prop spread.
 */
export const FavouritesPickerOptionLabel = ({
  className,
  ...props
}: FavouritesPickerOptionLabelProps) => (
  <label
    data-slot="favourites-picker-option-label"
    className={clsxm(favouritesPickerOptionLabelClasses, className)}
    {...props}
  />
);

/** A list's name, set at the size the copy around it runs at. */
const favouritesPickerOptionNameClasses = 'text-base/5';

export type FavouritesPickerOptionNameProps = ComponentProps<'span'>;

/** What a list is called. */
export const FavouritesPickerOptionName = ({
  className,
  ...props
}: FavouritesPickerOptionNameProps) => (
  <span
    data-slot="favourites-picker-option-name"
    className={clsxm(favouritesPickerOptionNameClasses, className)}
    {...props}
  />
);

/** The place the panel holds while the lists are still on their way. */
const favouritesPickerPendingClasses =
  'text-foreground-muted flex items-center justify-center py-4';

export type FavouritesPickerPendingProps = ComponentProps<'div'>;

/**
 * The waiting place.
 *
 * What spins in it is composed in — a spinner is artwork, and artwork is the
 * tenant's.
 */
export const FavouritesPickerPending = ({ className, ...props }: FavouritesPickerPendingProps) => (
  <div
    data-slot="favourites-picker-pending"
    className={clsxm(favouritesPickerPendingClasses, className)}
    {...props}
  />
);

/** What the panel says when the shopper has made no list yet: a mark and a sentence. */
const favouritesPickerEmptyClasses = 'text-foreground-muted flex gap-2';

export type FavouritesPickerEmptyProps = ComponentProps<'div'>;

/** The panel's empty state. */
export const FavouritesPickerEmpty = ({ className, ...props }: FavouritesPickerEmptyProps) => (
  <div
    data-slot="favourites-picker-empty"
    className={clsxm(favouritesPickerEmptyClasses, className)}
    {...props}
  />
);

/** The empty state's sentence, set small beside its mark. */
const favouritesPickerEmptyTextClasses = 'text-sm/4';

export type FavouritesPickerEmptyTextProps = ComponentProps<'span'>;

/** What the empty state says. */
export const FavouritesPickerEmptyText = ({
  className,
  ...props
}: FavouritesPickerEmptyTextProps) => (
  <span
    data-slot="favourites-picker-empty-text"
    className={clsxm(favouritesPickerEmptyTextClasses, className)}
    {...props}
  />
);
